import React from "react";
import Seo from "@/utils/seo";
import BannerCard from "@/components/ui/banner-card";
import Problem from "@/components/pages/initiatives/problem";
import Solution from "@/components/pages/initiatives/solution";
import SolutionGrid from "@/components/pages/initiatives/solution-grid";
// import Divider from "@/components/pages/initiatives/divider";
import Head from "next/head";
// import CaseStories from "@/components/pages/cri/case-stories";
import WhereWeWork from "@/components/pages/home/where-we-work";
import CaseStories from "@/components/pages/mrc/case-stories";

export default function CRI() {
  const seo = {
    slug: "/global-climate-resilience-collective",
    metaTitle:
      "Climate Resilience for Workers & Communities | People’s Courage International, USA",
    metaDescription:
      "The mission of our initiative is to build the resilience of climate-affected informal workers by ensuring their access to climate-responsive social protection and adaptation supports.",
  };
  const jsonLd = {
    "@context": "http://www.schema.org",
    "@type": "Organization",
    name: "Climate Resilience for Workers & Communities | People’s Courage International, USA",
    description:
      "The mission of our initiative is to build the resilience of climate-affected informal workers by ensuring their access to climate-responsive social protection and adaptation supports.",
    address: {
      "@type": "PostalAddress",
      streetAddress: "3206 10th Street NE, Washington",
      addressLocality: "DC",
      postalCode: "20017",
      addressCountry: "USA",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1202-573-8727",
      contactType: "customer support",
    },
  };
  const points = [
    {
      heading: "Investment gap",
      description:
        "Less than 20% of climate adaptation needs are funded. And less than 5% of climate funding goes to adaptation.",
      icon: "/images/icons/investment-gap.png",
    },
    {
      heading: "Design and innovation gap",
      description:
        "Even where there is investment, interventions mostly focus on responses to climate extremes. There is limited focus on slow onset events (e.g., heat, droughts) and the medium- to long-term impacts that affect 100s of millions.",
      icon: "/images/icons/design-gap.png",
    },
    {
      heading: "Invisibility of workers and lack of a people-first approach",
      description:
        "Climate policy is designed far from those who are experiencing the worst outcomes of climate change. Vulnerable communities in South and Southeast Asia are often invisible in the informal workforce.",
      icon: "/images/icons/worker (1).png",
    },
  ];
  return (
    <>
      <Seo seo={seo} />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(jsonLd),
          }}
        />
      </Head>
      <BannerCard
        banner={{
          title: "Climate Resilience for Workers & Communities",
          image: "/images/cli/cri-banner.webp",
        }}
        isActive={true}
        variant="single"
      />
      <Problem
        image="/images/cli/cri-problem.webp"
        descriptionOne="Climate change may push more than 720 million people back into poverty by 2050. It may also force 140 million people to become climate refugees. For informal workers and vulnerable communities in South and Southeast Asia, increased climate events have cascading impacts: further income insecurity (e.g., ~43 million jobs lost in South Asia), forced migration (e.g., 20 additional million climate migrants by 2050 in Bangladesh alone), food and water insecurity, increase in morbidity and excess deaths, and increased inequality. Some of these impacts will materialize even in low-warming scenarios, especially with the lack of social protections."
        descriptionTwo="These communities hardly contribute to climate change, but bear the brunt of its impact. This, even as they and their perspectives are absent from global forums and development of transition policies."
        descriptionThree="To change this, we believe there is a need to address three inter-related problems:"
        points={points}
      />

      <Solution
        image="/images/cli/sol-cli.webp"
        description="Our mission is to build the resilience of climate-affected informal workers by ensuring their access to climate-responsive social protection and adaptation supports. We aim to serve these communities across India, Bangladesh, Nepal, Indonesia, and Philippines, and build narratives and promote models that resonate far beyond. Key components of our work include:"
      />
      <SolutionGrid
        columns={3}
        data={[
          {
            title: "Narrative building",
            image: "/images/icons/writing.png",
            description: `Promote a people-first narrative by: <br/> <ul>
        <li>Elevating stories and clear data about who needs to be supported in contrast to using a single siloed-lens (e.g., climate adaptation, labor rights)</li>
        <li>Prioritizing bringing their voices into strategy development, and getting the global community to hear from them on solutions</li>
      </ul>`,
          },
          {
            title: "Project innovation & scaling",
            image: "/images/icons/collaborate.png",
            description: `Promote people-centric approaches by: <br/> <ul>
        <li>Developing on-ground projects with grassroots organizations based on community demand</li>
        <li>Building the evidence base of people-centric solutions</li>
        <li>Growing the base of grassroots actors working on these issues</li>
      </ul>`,
          },
          {
            title: "Advocacy for systems change",
            image: "/images/icons/system.png",
            description: `Drive investment by philanthropies, bi/multi-laterals, industry, and governments by: <br/> <ul>
        <li>Clarifying what works on the ground</li>
        <li>Advocating for delivery and financing models that can be implemented sustainably / systemically</li>
      </ul>`,
          },
        ]}
      />
      <WhereWeWork />
      <CaseStories />
      {/* <CaseStories /> */}
      {/* <Divider description="To address the large-scale prevalence of gender-based violence and the barriers faced by survivors to access justice across South Asia, People’s Courage International is working with organizations that collaborate with governments, civil society organizations, philanthropies, and other stakeholders towards building better response mechanisms at the regional level. People’s Courage International envisions 100 sexual violence-free districts across South Asia." /> */}
    </>
  );
}
